import ChevronFilters from '@assets/icons/chevron-down.svg';
import he from 'he';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  PixelRatio,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { Buffer } from 'buffer';
import { Button } from './button';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import {
  GetPrintTagTemplateForRestaurantResponse,
  PrintTagPreviewDisplayField,
  TagPrintingRenderTemplateQuery,
  TagPrintingRenderTemplateQueryVariables,
  TagPrintingTemplateQuery,
  TagPrintingTemplateQueryVariables,
} from '@/types';
import { gql } from '@apollo/client';
import { ItemMenuOperationsContent } from './orders/item-menu-operations-content';
import { PriceTypes } from '@/enums';
import { roundMicrosToNearestWholeCent } from '@/utils/money';
import { SelectInput } from './select-input';
import {
  TagPrintingTemplateCarousel,
  TemplateListOption,
} from './tag-printing-template-carousel';
import { TagPrintingTemplateCustomPriceField } from './tag-printing-template-custom-price-field';
import {
  TagPrintingTemplateMetadata,
  UserDefinedMetadata,
} from './tag-printing-types';
import { TEXT_INPUT_HEIGHT } from './text-input';
import { Type } from './toast';
import { useBluetoothContext } from '@/contexts/bluetooth-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const TAG_PRINTING_TEMPLATE_QUERY = gql`
  fragment TagPrintingVariantPackFields on variants_packs {
    restaurant_variant_pack_overrides(
      where: { restaurant_uuid: { _eq: $restaurantUUID } }
    ) {
      user_defined_metadata
      uuid
    }
    uuid
  }

  fragment PrintTagTemplatesFields on GetPrintTagTemplateForRestaurantResponse {
    preview_display_fields {
      key
      value
    }
    print_tag_template {
      display_name
      name
      preview_image_url_list
      uuid
      metadata
    }
  }

  query TagPrintingTemplate(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUIDs: [uuid!]!
  ) {
    visible_variant_packs(
      where: {
        variant_pack_uuid: { _in: $variantPackUUIDs }
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      variant_pack {
        ...TagPrintingVariantPackFields
      }
    }

    getPrintTagTemplatesForRestaurant(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_ids: $variantPackUUIDs
    ) {
      ...PrintTagTemplatesFields
      variant_pack_id
    }
  }
`;

export const TAG_PRINTING_RENDER_TEMPLATE_QUERY = gql`
  query TagPrintingRenderTemplate(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $printTagTemplateUUID: uuid!
    $variantPackInputs: [VariantPackInput!]!
  ) {
    renderPrintTagTemplateForRestaurant(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      print_tag_template_id: $printTagTemplateUUID
      variant_pack_inputs: $variantPackInputs
    ) {
      print_tag_template_id
      rendered_template
    }
  }
`;

/**
 * Props for the TagPrintingTemplateSelection component.
 */
export interface TagPrintingTemplateSelectionProps {
  customPriceInMicros: number | null;
  selectedTemplateUUID: string | null;
  selectedVariantPackUUID: string;
  selectPrinter: () => void;
  setCustomPriceInMicros: React.Dispatch<React.SetStateAction<number | null>>;
  setSelectedTemplateUUID: React.Dispatch<React.SetStateAction<string | null>>;
}

const buildTemplateItemListOption = (
  template: GetPrintTagTemplateForRestaurantResponse,
): TemplateListOption => ({
  metadata: {
    previewImageUrlList: template.print_tag_template?.preview_image_url_list,
  },
  title:
    template.print_tag_template?.display_name ||
    template.print_tag_template?.name!,
  value: template.print_tag_template?.uuid!,
});

export const getAvailablePriceTypes = (
  features: TagPrintingTemplateMetadata['features'],
): PriceTypes[] => {
  const priceTypes: PriceTypes[] = [];

  if (features) {
    if (features.set_retail_price_to_msrp_enabled) {
      priceTypes.push(PriceTypes.MSRP);
    }
    if (features.set_retail_price_to_srp_enabled) {
      priceTypes.push(PriceTypes.SRP);
    }
  }

  return priceTypes;
};

/**
 * Component for selecting a tag printing template.
 * @param {TagPrintingTemplateSelectionProps} props - The component props.
 */
export const TagPrintingTemplateSelection: React.FC<
  TagPrintingTemplateSelectionProps
> = ({
  customPriceInMicros,
  selectedTemplateUUID,
  setSelectedTemplateUUID,
  selectPrinter,
  selectedVariantPackUUID,
  setCustomPriceInMicros,
}) => {
  const [styles, theme] = useStyles();
  const windowDimensions = useWindowDimensions();
  const { isSmallLayout } = useLayoutContext();
  const { clearSelectedDevice, selectedDevice } = useBluetoothContext();
  const { showErrorToast, showToast } = useToasts();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const templateHydratedRef = useRef(false);
  const priceHydratedRef = useRef(false);
  const listRef = useRef<FlatList<TemplateListOption>>(null);
  const SELECTED_TEMPLATE_SCROLL_VIEW_HEIGHT = isSmallLayout
    ? windowDimensions.height * 0.25
    : windowDimensions.height * 0.3;

  const { data, error, loading } = useWrappedQuery<
    TagPrintingTemplateQuery,
    TagPrintingTemplateQueryVariables
  >(TAG_PRINTING_TEMPLATE_QUERY, {
    fetchPolicy: 'no-cache',
    variables: {
      restaurantUUID,
      supplierUUID,
      variantPackUUIDs: [selectedVariantPackUUID],
    },
  });

  const renderTemplateQuery = useLazyQuery<
    TagPrintingRenderTemplateQuery,
    TagPrintingRenderTemplateQueryVariables
  >(TAG_PRINTING_RENDER_TEMPLATE_QUERY, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (error) {
      showErrorToast();
    }
  }, [error, showErrorToast]);

  const templateCarouselData = useMemo(
    () =>
      data?.getPrintTagTemplatesForRestaurant.reduce<TemplateListOption[]>(
        (acc, template) => {
          acc.push(
            buildTemplateItemListOption(
              template as GetPrintTagTemplateForRestaurantResponse,
            ),
          );
          return acc;
        },
        [],
      ) ?? [],
    [data?.getPrintTagTemplatesForRestaurant],
  );

  const variantPackOverrides =
    data?.visible_variant_packs?.[0]?.variant_pack
      ?.restaurant_variant_pack_overrides?.[0] || null;

  useEffect(() => {
    if (!variantPackOverrides || priceHydratedRef.current) {
      return;
    }

    const initialRetailPriceInMicros =
      variantPackOverrides?.user_defined_metadata?.list_prices
        ?.retail_price_in_micros;

    if (initialRetailPriceInMicros) {
      setCustomPriceInMicros(initialRetailPriceInMicros);
    }

    priceHydratedRef.current = true;
  }, [variantPackOverrides, setCustomPriceInMicros]);

  useEffect(() => {
    const templateSectionData = templateCarouselData;
    const numberOfTemplates = templateSectionData.length;
    if (numberOfTemplates === 0 || templateHydratedRef.current) {
      return;
    }

    if (numberOfTemplates === 1) {
      setSelectedTemplateUUID(templateSectionData[0].value as string);
      templateHydratedRef.current = true;
      return;
    }

    const userDefinedMetadata = variantPackOverrides?.user_defined_metadata as
      | UserDefinedMetadata
      | undefined;

    const preselectedTemplateUUID =
      userDefinedMetadata?.print_tag_attributes
        ?.selected_print_tag_template_uuid;
    if (preselectedTemplateUUID) {
      const foundTemplate = templateSectionData.find(
        (template) => template.value === preselectedTemplateUUID,
      );
      if (foundTemplate) {
        setSelectedTemplateUUID((prev) =>
          prev ? prev : foundTemplate.value.toString(),
        );
      }
    }
    templateHydratedRef.current = true;
  }, [setSelectedTemplateUUID, variantPackOverrides, templateCarouselData]);

  const handleTemplateSelection = useCallback(
    (item: TemplateListOption) => {
      if (templateCarouselData.length === 1) {
        return;
      }

      setSelectedTemplateUUID((prevSelected) =>
        prevSelected === item.value ? null : (item.value as string),
      );
    },
    [setSelectedTemplateUUID, templateCarouselData],
  );

  /**
   * Executes the print action by rendering the selected template and sending it to the connected device.
   * Handles errors and device connectivity issues gracefully.
   */
  const handlePrint = useCallback(async () => {
    try {
      const retailPriceInMicros = customPriceInMicros
        ? roundMicrosToNearestWholeCent(customPriceInMicros)
        : undefined;

      const { data, error } = await renderTemplateQuery({
        variables: {
          printTagTemplateUUID: selectedTemplateUUID!,
          restaurantUUID,
          supplierUUID,
          variantPackInputs: [
            {
              user_input: {
                retail_price_in_micros: retailPriceInMicros,
              },
              variant_pack_id: selectedVariantPackUUID,
            },
          ],
        },
      });

      if (error) {
        showErrorToast();
        return;
      }

      const printContent = data?.renderPrintTagTemplateForRestaurant?.find(
        (template) => template.print_tag_template_id === selectedTemplateUUID,
      )?.rendered_template;

      if (!printContent) {
        return;
      }

      const isConnected =
        selectedDevice && (await selectedDevice.isConnected());
      if (!isConnected || !selectedDevice) {
        clearSelectedDevice();
        showToast({
          content: i18n.t('bluetooth_device.disconnected'),
          type: Type.WARNING,
        });
        selectPrinter();
        return;
      }
      const printBuffer = Buffer.from(printContent).toString('base64');
      await selectedDevice.write(printBuffer, 'base64');
    } catch (e) {
      showErrorToast();
      captureException(e);
    }
  }, [
    customPriceInMicros,
    renderTemplateQuery,
    selectedTemplateUUID,
    restaurantUUID,
    supplierUUID,
    selectedVariantPackUUID,
    selectedDevice,
    clearSelectedDevice,
    showToast,
    selectPrinter,
    showErrorToast,
  ]);

  const selectedTemplateData = useMemo(
    () =>
      data?.getPrintTagTemplatesForRestaurant?.find(
        (template) =>
          template.print_tag_template?.uuid === selectedTemplateUUID,
      ),
    [data?.getPrintTagTemplatesForRestaurant, selectedTemplateUUID],
  );

  useEffect(() => {
    if (listRef.current && selectedTemplateUUID) {
      const foundTemplateIndex = templateCarouselData.findIndex(
        (template) => template.value === selectedTemplateUUID,
      );
      if (foundTemplateIndex !== -1) {
        listRef.current.scrollToIndex({
          animated: true,
          index: foundTemplateIndex,
        });
      }
    }
  }, [selectedTemplateUUID, templateCarouselData]);

  // Calculate the minimum height for the ScrollView based on maximum field counts
  const scrollViewHeight = useMemo(() => {
    const initialCounts = { custom: 0, normal: 0 };

    const FIELD_ROW_HEADER_HEIGHT = 21 * PixelRatio.getFontScale(); // Line height + margin bottom = 21px
    const FIELD_VALUE_HEADER_HEIGHT =
      TYPOGRAPHY.pSemiBold.lineHeight * PixelRatio.getFontScale();
    const NORMAL_FIELD_ROW_HEIGHT =
      FIELD_ROW_HEADER_HEIGHT + FIELD_VALUE_HEADER_HEIGHT; // Total height for normal fields
    const CUSTOM_FIELD_ROW_HEIGHT =
      FIELD_ROW_HEADER_HEIGHT + TEXT_INPUT_HEIGHT * PixelRatio.getFontScale(); // Total height for custom fields
    const maxFieldCounts =
      data?.getPrintTagTemplatesForRestaurant?.reduce((counts, template) => {
        counts.normal = Math.max(
          counts.normal,
          template.preview_display_fields.length,
        );
        counts.custom = Math.max(
          counts.custom,
          Object.keys(template.print_tag_template?.metadata.features ?? {})
            .length,
        );
        return counts;
      }, initialCounts) ?? initialCounts;

    return Math.min(
      SELECTED_TEMPLATE_SCROLL_VIEW_HEIGHT,
      maxFieldCounts.normal * NORMAL_FIELD_ROW_HEIGHT +
        maxFieldCounts.custom * CUSTOM_FIELD_ROW_HEIGHT,
    );
  }, [
    SELECTED_TEMPLATE_SCROLL_VIEW_HEIGHT,
    data?.getPrintTagTemplatesForRestaurant,
  ]);

  return (
    <ItemMenuOperationsContent
      hideCloseButton
      onClose={() => {}}
      operationName="print_tag"
    >
      <View style={styles.base}>
        {loading ? (
          <ActivityIndicator />
        ) : (
          <>
            <TagPrintingTemplateCarousel
              onTemplateSelect={handleTemplateSelection}
              selectedTemplateUUID={selectedTemplateUUID}
              templateOptions={templateCarouselData}
            />
            {selectedTemplateData ? (
              <ScrollView
                style={[
                  styles.fieldContainer,
                  {
                    height: scrollViewHeight,
                  },
                ]}
              >
                {selectedTemplateData.preview_display_fields.map(
                  (field: PrintTagPreviewDisplayField, index: number) => (
                    <View
                      key={`${field.key}-${index}`}
                      style={styles.fieldRow}
                      testID={`${field.key}-${index}`}
                    >
                      <Text style={styles.fieldKey}>{field.key}</Text>
                      <Text style={[TYPOGRAPHY.p, styles.fieldValue]}>
                        {he.decode(field.value)}
                      </Text>
                    </View>
                  ),
                )}
                {selectedTemplateData.print_tag_template?.metadata.features
                  ?.retail_price_enabled && priceHydratedRef.current ? (
                  <TagPrintingTemplateCustomPriceField
                    availablePriceTypes={getAvailablePriceTypes(
                      selectedTemplateData.print_tag_template?.metadata
                        .features,
                    )}
                    customPriceInMicros={customPriceInMicros}
                    setCustomPriceInMicros={setCustomPriceInMicros}
                    variantPackUUID={selectedVariantPackUUID}
                  />
                ) : null}
              </ScrollView>
            ) : null}
            <SelectInput
              inputContainerStyle={[
                selectedTemplateUUID ? null : styles.actionButton,
                styles.selectButton,
              ]}
              label={selectedDevice ? '' : i18n.t('select_printer')}
              onPress={selectPrinter}
              rightElement={
                <ChevronFilters
                  fill={theme.colors.primary}
                  height={8}
                  width={14}
                />
              }
              value={selectedDevice ? selectedDevice.name : ''}
            />
            <Button
              disabled={!selectedDevice || !selectedTemplateUUID}
              onPress={handlePrint}
              style={styles.actionButton}
              trackingData={{
                name: 'item_details.print_tag_button',
                properties: {
                  templateUUID: selectedTemplateUUID,
                },
              }}
            >
              {i18n.t('print')}
            </Button>
          </>
        )}
      </View>
    </ItemMenuOperationsContent>
  );
};

const useStyles = createStyles((theme) => ({
  actionButton: {
    marginTop: 16,
  },
  base: {
    flexDirection: 'column',
  },
  fieldContainer: {
    gap: 4,
    marginVertical: 16,
  },
  fieldKey: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.secondary,
    fontSize: 14,
    marginBottom: 4,
  },
  fieldRow: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    marginBottom: 4,
  },
  fieldValue: {
    ...FONTS.primary,
    marginBottom: 4,
  },
  selectButton: {
    borderColor: theme.colors.stroke,
  },
}));
